import { RuleSection } from '../types';

export const interactionSections: RuleSection[] = [
  {
    id: 'interactions-order',
    title: 'Interakcje — kolejność działań',
    requiredModuleIds: ['base'],
    editionTag: 'C3',
    sourceIds: ['wica-order', 'corpus-base'],
    keywords: ['interakcje', 'kolejnosc', 'tura'],
    level: 'advanced',
    hubGroup: 'Interakcje',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym rozdziale',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy w jednej partii gra kilka dodatków, efekty mogą domagać się uwagi przed kafelkiem, po wystawieniu figury albo po punktowaniu. Ten rozdział ustala wspólny szkielet tury, żebyście nie rozstrzygali smoka po punktowaniu ani budowniczego w środku ruchu smoka. Szczegóły każdego dodatku pozostają w jego własnym rozdziale — tutaj liczy się kolejność.',
      },
      {
        type: 'heading',
        text: 'Kiedy stosować ten szkielet',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Używaj go zawsze, gdy aktywny jest więcej niż jeden moduł wpływający na turę. Jeśli masz tylko podstawę, wystarczy zwykła kolejność: kafelek → opcjonalna figura → ukończenia. Szkielet nie zmienia wartości punktowych; jedynie mówi, w jakiej kolejności rozpatrywać triggery.',
      },
      {
        type: 'resolutionOrder',
        title: 'Szkielet tury przy wielu dodatkach',
        steps: [
          'Efekty przed dobraniem lub dołożeniem kafelka (smok, złodziej, oblężenie i podobne).',
          'Dołożenie kafelka, ratusza albo innego elementu zastępującego kafelek.',
          'Umieszczenie figury albo akcja wykonywana zamiast wystawienia.',
          'Punktowania ukończonych obszarów oraz triggery zależne od ukończenia (np. świnia, występ).',
          'Efekty po punktowaniu (np. wróżka) oraz ewentualna dodatkowa tura budowniczego dopiero po zakończeniu bieżącej sekwencji.',
        ],
      },
      {
        type: 'example',
        title: 'Przykład przy stole',
        text: 'Bartek dobiera kafelek ze symbolem smoka przy aktywnych Smoku i Budowniczym. Najpierw wykonuje dwa proste ruchy smoka i usuwa napotkane niechronione figury. Dopiero potem dokłada kafelek, ewentualnie wystawia figurę i punktuje ukończenia. Jeśli w tej turze przedłużył obszar z własnym budowniczym, dodatkową turę zaczyna dopiero po pełnym zakończeniu tej sekwencji.',
      },
      {
        type: 'exception',
        text: 'Konkretny dodatek może wskazać węższy moment (np. „przed dołożeniem kafelka”). Wtedy stosuj zapis tego dodatku wewnątrz odpowiedniego kroku szkieletu, a nie poza nim.',
      },
      {
        type: 'warning',
        text: 'Przy konflikcie kolejności: oficjalny opis danego dodatku C3.1, potem FAQ C3.1, potem ten korpus. Nie improwizuj kolejności „na logikę”.',
      },
      {
        type: 'commonMistake',
        text: 'Punktowanie ukończonego miasta przed ruchem smoka wywołanym symbolem na dobranym kafelku. Korekta: najpierw efekty przed kafelkiem, potem dołożenie i dopiero potem punktowanie.',
      },
      {
        type: 'crossReference',
        sectionId: 'turn-structure',
        label: 'Podstawowa kolejność tury bez dodatków',
      },
      {
        type: 'crossReference',
        sectionId: 'interactions-dragon-builder',
        label: 'Szczegół: smok i budowniczy',
      },
      {
        type: 'heading',
        text: 'Krótko',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Najpierw triggery przed kafelkiem, potem kafelek i figura.',
          'Punktowanie ukończeń następuje po wystawieniu.',
          'Dodatkowa tura budowniczego — dopiero po pełnej sekwencji.',
          'Szczegóły wartości zostają w rozdziałach dodatków.',
        ],
      },
      {
        type: 'qa',
        question: 'Czy mogę najpierw dołożyć kafelek ze smokiem, a smoka ruszyć później?',
        answer:
          'Nie. Symbol smoka uruchamia ruchy przed dołożeniem tego kafelka. Dopiero po ruchach smoka orientujesz i dokładasz kafelek.',
      },
      {
        type: 'qa',
        question: 'Kiedy zaczyna się dodatkowa tura budowniczego?',
        answer:
          'Dopiero gdy bieżąca tura — łącznie z punktowaniem i efektami po nim — zostanie dokończona. Nie przerywaj ruchu smoka ani punktowania, by od razu grać drugą turę.',
      },
    ],
  },
  {
    id: 'interactions-dragon-builder',
    title: 'Interakcje — smok i budowniczy',
    requiredModuleIds: [
      'major-03-dragon-fairy',
      'major-02-traders-builders',
    ],
    editionTag: 'C3.1',
    sourceIds: ['official-03', 'official-02'],
    keywords: ['smok', 'budowniczy'],
    level: 'advanced',
    hubGroup: 'Interakcje',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym rozdziale',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Smok i budowniczy często spotykają się w tej samej turze. Smok grozi figurami przed dołożeniem kafelka; budowniczy nagradza przedłużenie własnego obszaru dodatkową pełną turą. Ten rozdział łączy oba momenty bez zmiany ich osobnych zasad.',
      },
      {
        type: 'paragraph',
        text: 'Ruchy smoka wykonujesz przed dołożeniem kafelka ze symbolem. Budowniczy uruchamia dodatkową turę dopiero po zakończeniu całej bieżącej sekwencji — w tym po punktowaniu. W dodatkowej turze smok może znów się ruszyć, jeśli dobierzesz kolejny symbol smoka.',
      },
      {
        type: 'example',
        title: 'Przykład',
        text: 'Anna ma budowniczego na drodze i dobiera kafelek ze smokiem, który jednocześnie przedłuża tę drogę. Najpierw rusza smokiem (dwa proste ruchy). Potem dokłada kafelek, opcjonalnie wystawia figurę i punktuje. Dopiero gdy ta tura jest domknięta, budowniczy wraca i Anna rozgrywa dodatkową pełną turę.',
      },
      {
        type: 'exception',
        text: 'Sam ruch smoka, portal albo punktowanie bez przedłużenia obszaru kafelkiem nie dają dodatkowej tury budowniczego.',
      },
      {
        type: 'commonMistake',
        text: 'Zaczynanie dodatkowej tury w trakcie ruchu smoka. Korekta: najpierw dokończ całą bieżącą sekwencję, potem graj drugą turę.',
      },
      {
        type: 'crossReference',
        sectionId: 'm03-dragon',
        label: 'Procedura smoka',
      },
      {
        type: 'crossReference',
        sectionId: 'm02-builder',
        label: 'Procedura budowniczego',
      },
      {
        type: 'heading',
        text: 'Krótko',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Smok: przed kafelkiem.',
          'Budowniczy: po pełnej turze.',
          'Druga tura może znów wyciągnąć smoka.',
        ],
      },
      {
        type: 'qa',
        question: 'Czy smok w dodatkowej turze działa inaczej?',
        answer:
          'Nie. Jeśli w dodatkowej turze dobierzesz kafelek ze symbolem smoka, znów wykonujesz dwa proste ruchy przed dołożeniem, według zwykłych zasad dodatku.',
      },
    ],
  },
  {
    id: 'interactions-fields-special',
    title: 'Interakcje — pola specjalne',
    requiredModuleIds: ['base'],
    editionTag: 'C3',
    sourceIds: ['corpus-base'],
    keywords: ['pole', 'pasterz', 'strach', 'swinia', 'stodola'],
    level: 'advanced',
    hubGroup: 'Interakcje',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym rozdziale',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podstawowe pola punktują wyłącznie na końcu przez chłopów. Dodatki mogą postawić na polu inne figury albo rozliczyć pole wcześniej. Ten rozdział pilnuje, byście nie mieszali końcowego scoringu chłopów z triggerami pasterza, stracha na wróble, świni ani stodoły.',
      },
      {
        type: 'paragraph',
        text: 'Pasterz (Pasterze i Owce) zajmuje pole i blokuje wystawienie chłopa na tym samym polu. Strach na wróble i świnia z Kurierów/Burmistrzów oraz Kupców mają własne momenty punktowania opisane w swoich rozdziałach — nie zastępują końcowych 3 punktów za ukończone miasto. Stodoła (Zamki i Mosty) rozlicza farmerów w momencie budowy według własnej procedury.',
      },
      {
        type: 'bulletList',
        items: [
          'Pasterz blokuje stawianie chłopa na swoim polu.',
          'Strach na wróble i świnia C3.1 mają własne triggery — nie myl z końcowym scoringiem chłopów.',
          'Stodoła punktuje farmerów w momencie budowy, według rozdziału Zamków i Mostów.',
          'Bez aktywnego dodatku pole nadal punktuje tylko na końcu: 3 za ukończone miasto.',
        ],
      },
      {
        type: 'example',
        title: 'Przykład',
        text: 'Na polu stoi pasterz Anny. Bartek nie może wystawić tam chłopa. Gdy później Anna zbierze stado według zasad Pasterzy, rozlicza owce według tego dodatku; to nie jest jeszcze końcowe punktowanie chłopów na pozostałych polach.',
      },
      {
        type: 'commonMistake',
        text: 'Dodawanie 3 punktów za miasto przy rozliczeniu stada lub świni „bo to też pole”. Korekta: stosuj wyłącznie procedurę aktywnego dodatku; końcowe 3 punkty zostają dla chłopów przy punktowaniu końcowym.',
      },
      {
        type: 'crossReference',
        sectionId: 'fields',
        label: 'Podstawowe punktowanie pól',
      },
      {
        type: 'heading',
        text: 'Krótko',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Chłop = koniec gry.',
          'Figury dodatków na polu mają własne triggery.',
          'Nie sumuj premii z różnych systemów „na oko”.',
        ],
      },
      {
        type: 'qa',
        question: 'Czy pasterz dostaje 3 punkty za ukończone miasta jak chłop?',
        answer:
          'Nie. Pasterz rozlicza stado według Pasterzy i Owiec. Końcowe 3 punkty za miasto należą do chłopów na polach bez sprzecznej figury dodatku.',
      },
    ],
  },
  {
    id: 'interactions-warning-mix',
    title: 'Interakcje — ostrzeżenie o mieszaniu',
    requiredModuleIds: ['base'],
    editionTag: 'C3',
    sourceIds: ['corpus-base'],
    keywords: ['edycja', 'c3.1', 'mieszanie'],
    level: 'basic',
    hubGroup: 'Interakcje',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym rozdziale',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najczęstszy błąd zaawansowanych graczy to mieszanie pamięci C1/C2 z procedurami C3.1, bo tytuły brzmią podobnie. Ten rozdział jest checklistą przed partią z wieloma dodatkami.',
      },
      {
        type: 'editionWarning',
        text: 'Nigdy nie łącz w jednej partii reguł C3.1 z pamięcią C1/C2 „bo tytuł podobny”.',
        oldEditionName: 'wszystkie stare odpowiedniki',
      },
      {
        type: 'checklist',
        items: [
          'Sprawdź aktywne moduły w konfiguracji aplikacji.',
          'Przeczytaj editionWarning na początku rozdziału każdego dodatku.',
          'Użyj Szybkiej ściągi przefiltrowanej do aktywnych majorów.',
          'Nie przenoś porywania meepli, bazarów, wzgórz ani starego ruchu smoka „po sześć”.',
        ],
      },
      {
        type: 'commonMistake',
        text: 'Granie Wieżami C3.1 jak starą Wieżą z porywaniem. Korekta: w C3.1 obowiązują strażnicy i złodziej — bez porywania meepli z mapy.',
      },
      {
        type: 'crossReference',
        sectionId: 'edition-profile',
        label: 'Profil edycji C3 / C3.1',
      },
      {
        type: 'heading',
        text: 'Krótko',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Włączasz tylko to, co leży na stole.',
          'Tytuł ≠ stare zasady.',
          'W razie wątpliwości: rozdział dodatku C3.1, nie pamięć z C2.',
        ],
      },
      {
        type: 'qa',
        question: 'Czy mogę domyślnie grać wszystkimi dziesięcioma majorami naraz?',
        answer:
          'Technicznie możesz włączyć gotowe moduły, ale przy stole trzymaj się aktywnej konfiguracji i czytaj interakcje. Im więcej majorów, tym ważniejszy szkielet kolejności z rozdziału „kolejność działań”.',
      },
    ],
  },
  {
    id: 'interactions-towers-siege',
    title: 'Interakcje — wieże i oblężenie',
    requiredModuleIds: [
      'major-04-towers-thieves',
      'major-07-siege-defense',
    ],
    editionTag: 'C3.1',
    sourceIds: ['official-04', 'official-07'],
    keywords: ['wieza', 'twierdza', 'obleznie'],
    level: 'advanced',
    hubGroup: 'Interakcje',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tym rozdziale',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wieże i Oblężenie wpływają na kontrolę mapy w różny sposób. Wieża buduje zasięg i strażników; Oblężenie operuje stanowiskami obrony, katapultami i twierdzą. Ten rozdział mówi, jak je czytać razem, bez importowania starego porywania meepli.',
      },
      {
        type: 'paragraph',
        text: 'Twierdza liczy się jako ukończone miasto dla farmerów i może zajmować wiele sąsiednich kafelków przy ocenie zasięgów opisanych w dodatkach. Strażnicy z Wież nie porywają figur z mapy. Atak oblężenia może usuwać pasujące figury według własnej procedury Oblężenia — to nie jest efekt wieży.',
      },
      {
        type: 'example',
        title: 'Przykład',
        text: 'Na stole stoi wieża ze strażnikiem i osobno twierdza z figurami na stanowisku obrony. Rozliczenie złodzieja dotyczy wieży i strażnika według Wież. Osobny symbol oblężenia rozpatruje się przed kafelkiem według Oblężenia. Nie przenoś wyniku jednego triggera na drugi.',
      },
      {
        type: 'commonMistake',
        text: 'Usuwanie meepli zasięgiem wieży „jak w starej Wieży”. Korekta: Wieże C3.1 nie porywają; usuwanie figur przy ataku należy wyłącznie do procedur Oblężenia.',
      },
      {
        type: 'crossReference',
        sectionId: 'm04-towers',
        label: 'Wieże i strażnicy',
      },
      {
        type: 'crossReference',
        sectionId: 'm07-attack',
        label: 'Atak oblężenia',
      },
      {
        type: 'heading',
        text: 'Krótko',
        level: 2,
      },
      {
        type: 'bulletList',
        items: [
          'Wieża ≠ porywanie.',
          'Oblężenie ma własne stanowiska i ataki.',
          'Twierdza pomaga farmerom jak ukończone miasto.',
        ],
      },
      {
        type: 'qa',
        question: 'Czy strażnik chroni przed atakiem oblężenia?',
        answer:
          'Nie automatycznie. Strażnik działa w procedurze Wież. Obrona przed oblężeniem korzysta ze stanowisk i figur opisanych w Oblężeniu i Obronie.',
      },
    ],
  },
];
